import legacyPrintMap from "./legacyPrintMap.json";
import { getFontById, getFontScale } from "./designerUtils";
import { drawShapedText } from "./drawShapedText";
import {
  applyPredesignSizeAttrs,
  getPrintInstanceOrder,
  isFixedPredesignSelection,
  isTintablePrint,
} from "./designerHelpers";
import {
  buildCustomCutRuntime,
} from "./designerCustomCuts";

// 🪤 REACT_APP_V3_PROD_URL apunta al server viejo: queda sólo por compatibilidad y el default
// ya es V4, que es quien sirve /img/master, /fonts/master y /uploads/charly desde el 2-10-2026.
const CDN_BASE = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_V3_PROD_URL || "https://v4.micuenta.blue";
// V3 se está apagando: las rutas RELATIVAS de storage de V4 (master-prints/…, master-parts/…, etc.)
// tienen que resolver contra V4, NO contra v3core. Antes toda relativa iba a CDN_BASE (v3core) → 404.
const V4_FILES_BASE = (process.env.REACT_APP_V4_FILES_URL || "https://v4.micuenta.blue").replace(/\/+$/, "");
const V4_STORAGE_RE = /^\/?(storage\/)?(master-prints|master-parts|masters|predesigns|sketches)\//i;
const resolveV4Storage = (src) => {
  const s = String(src || "").replace(/^\/+/, "");
  return `${V4_FILES_BASE}/storage/${s.replace(/^storage\//i, "")}`;
};
const { BASE_SIZE = 900, BASE_CM = 94, LEGACY_PRINT_MAP = {} } = legacyPrintMap;

// Escala REAL del print (px por cm de un talle 4): las máscaras 900×900 están dibujadas a
// ~8,0 px/cm (chomba talle 4: sisa a sisa 50 cm ≈ 400 px). O sea 900 px ≈ 112 cm, NO 94.
// Con BASE_CM=94 los prints salían grandes; con 112 quedan a tamaño real (un print de 25 cm
// = 50% de la sisa). Ver PROXIMA-SESION. El TEXTO se dimensiona por su altura real (drawPrint).
const PRINT_SCALE_CM = 112;
// Escala VERTICAL: el TAMAÑO de un print es su ALTO (regla del negocio). La espalda talle 4
// (~60 cm) se dibuja ~660 px → 11 px/cm. Un print de 25 cm ocupa 25×11 = 275 px ≈ 42% de la
// espalda. Tanto imágenes como texto se dimensionan por su alto real con esta escala.
const PRINT_SCALE_CM_H = 81.8;
const cmToWidthPercent = (cm) => (cm / PRINT_SCALE_CM) * 100;
// cmToPx sigue con BASE_CM (posicionamiento legacy del modelo vertical 70/30, línea ~183): NO tocar.
const cmToPx = (cm, size = BASE_SIZE) => (cm * size) / BASE_CM;

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

const comparePrintStackOrder = (aEntry, bEntry) => {
  const [aKey, aSelection] = aEntry;
  const [bKey, bSelection] = bEntry;
  const aOrder = getPrintInstanceOrder(aKey, aSelection);
  const bOrder = getPrintInstanceOrder(bKey, bSelection);
  if (aOrder !== bOrder) return aOrder - bOrder;
  return String(aKey).localeCompare(String(bKey));
};

const normalizeUrl = (src) => {
  if (!src) return null;
  if (src.startsWith("data:") || src.startsWith("blob:")) {
    return src;
  }
  if (src.startsWith("http")) {
    try {
      const u = new URL(src);
      if (["localhost", "127.0.0.1", "0.0.0.0"].includes(u.hostname)) {
        return `${CDN_BASE}${u.pathname}${u.search}`;
      }
      return src;
    } catch (e) {
      return src;
    }
  }
  // Ruta relativa de storage de V4 → V4 (no v3core, que está apagado).
  if (V4_STORAGE_RE.test(src)) return resolveV4Storage(src);
  return `${CDN_BASE}${src.startsWith("/") ? "" : "/"}${src}`;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const norm = normalizeUrl(src);
    if (!norm) {
      reject(new Error("src vacío"));
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = norm;
  });

const safeLoadImage = async (src) => {
  try {
    return await loadImage(src);
  } catch (e) {
    return null;
  }
};

const loadImageDirect = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error("src vacío"));
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });

const safeLoadImageDirect = async (src) => {
  try {
    return await loadImageDirect(src);
  } catch (e) {
    return null;
  }
};

const getImageOpaqueBounds = (img) => {
  const width = Math.max(1, Number(img?.naturalWidth || img?.width || 1));
  const height = Math.max(1, Number(img?.naturalHeight || img?.height || 1));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    return { x: 0, y: 0, width, height, aspectRatio: width / height };
  }
  ctx.drawImage(img, 0, 0, width, height);
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const alpha = data[((y * width) + x) * 4 + 3];
      if (alpha <= 8) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < minX || maxY < minY) {
    return { x: 0, y: 0, width, height, aspectRatio: width / height };
  }
  const boundedWidth = Math.max(1, (maxX - minX) + 1);
  const boundedHeight = Math.max(1, (maxY - minY) + 1);
  return {
    x: minX,
    y: minY,
    width: boundedWidth,
    height: boundedHeight,
    aspectRatio: boundedWidth / boundedHeight,
  };
};

const getLegacyMergeBgPath = (name) => {
  const pub = process.env.PUBLIC_URL || "";
  return `${pub}/img/master/${name}`;
};

const getVerticalMergeBgPath = (name) => {
  const pub = process.env.PUBLIC_URL || "";
  return `${pub}/img/master/${name}`;
};

const loadLegacyMergeBackgrounds = async () => {
  const names = ["bg1.png", "bg2.png", "bg3.png", "bg4.png"];
  const loaded = [];
  for (const name of names) {
    const localSrc = getLegacyMergeBgPath(name);
    const remoteSrc = `${CDN_BASE}/img/master/${name}`;
    const img = await safeLoadImageDirect(localSrc) || await safeLoadImageDirect(remoteSrc);
    if (img) loaded.push(img);
  }
  return loaded;
};

const drawLegacyMergedBackground = async (ctx, { x, y, size, height, layout = "horizontal" }) => {
  const bgs = await loadLegacyMergeBackgrounds();
  bgs.forEach((img, k) => {
    const rw = (img.width * size) / 500;
    const rh = (img.height * size) / 500;
    let dx = x;
    let dy = y;
    if (layout === "vertical") {
      if (k === 1) {
        dx = x;
        dy = y + height - img.height;
      } else if (k === 2) {
        dy = y;
        dx = x + size - img.width;
      } else if (k === 3) {
        dx = x + size - rw / 2;
        dy = y + (height / 2) - rh / 2;
      }
    } else {
      if (k === 1) {
        dx = x;
        dy = y + height - img.height;
      } else if (k === 2) {
        dy = y;
        dx = x + size * 2 - img.width;
      } else if (k === 3) {
        dx = x + size - rw / 2;
        dy = y + cmToPx(26, size);
      }
    }
    ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, rw, rh);
  });
};

const loadVerticalBackgroundAssets = async () => {
  const full = await safeLoadImageDirect(getVerticalMergeBgPath("bg-vertical-full.jpg"));
  if (full) {
    return { full };
  }

  const top = await safeLoadImageDirect(getVerticalMergeBgPath("bg-vertical-1.png"));
  const bottom = await safeLoadImageDirect(getVerticalMergeBgPath("bg-vertical-2.png"));
  const stripe = await safeLoadImageDirect(getVerticalMergeBgPath("bg-vertical-3.png"));
  return { full: null, top, bottom, stripe };
};

const drawVerticalBackground = async (ctx, { width, height }) => {
  const assets = await loadVerticalBackgroundAssets();
  if (assets.full) {
    ctx.drawImage(assets.full, 0, 0, assets.full.width, assets.full.height, 0, 0, width, height);
    return;
  }

  if (assets.top) {
    const topHeight = Math.round(height * 0.28);
    ctx.drawImage(assets.top, 0, 0, assets.top.width, assets.top.height, 0, 0, width, topHeight);
  }
  if (assets.bottom) {
    const bottomHeight = Math.round(height * 0.28);
    ctx.drawImage(assets.bottom, 0, 0, assets.bottom.width, assets.bottom.height, 0, height - bottomHeight, width, bottomHeight);
  }
  if (assets.stripe) {
    const stripeHeight = Math.round(height * 0.11);
    const stripeY = Math.round((height / 2) - (stripeHeight / 2));
    ctx.drawImage(assets.stripe, 0, 0, assets.stripe.width, assets.stripe.height, 0, stripeY, width, stripeHeight);
  }
};

const buildLayersForSide = (parts = [], side = "front") => {
  const layers = [];
  parts.forEach((p) => {
    const item = p.Item || {};
    const z = Number(item.z || item.z_index || 0);
    const colorHex = p.color?.code || p.color?.hex || null;
    const patternUrl = p.fullprintIcon || p.fullprintImage || null;
    const partKey = String(p?.key || "");
    const partLabel = String(item?.factory_name || item?.nname || item?.name || p?.name || "");
    if (side === "front") {
      if (item.img_front_t) layers.push({ kind: "mask", img: item.img_front_t, zIndex: z, colorHex, patternUrl, partKey, partLabel, item, node: p });
      if (item.img_front) layers.push({ kind: "image", img: item.img_front, zIndex: z + 2, partKey, partLabel, item, node: p });
    } else {
      if (item.img_back_t) layers.push({ kind: "mask", img: item.img_back_t, zIndex: z, colorHex, patternUrl, partKey, partLabel, item, node: p });
      if (item.img_back) layers.push({ kind: "image", img: item.img_back, zIndex: z + 2, partKey, partLabel, item, node: p });
    }
  });
  return layers.sort((a, b) => a.zIndex - b.zIndex);
};

const buildParentMap = (root) => {
  const map = {};
  const walk = (node, parent = null) => {
    if (!node) return;
    map[String(node.key)] = parent ? String(parent.key) : null;
    (node.children || []).forEach((c) => walk(c, node));
  };
  walk(root, null);
  return map;
};

const resolveSide = (node, parentMap, nodeMap, selSide = null) => {
  if (selSide !== null && selSide !== undefined) return Number(selSide);
  let cur = node;
  while (cur) {
    const item = cur.Item || {};
    const hasBack = item.img_back || item.img_back_t;
    const hasFront = item.img_front || item.img_front_t;
    if (hasBack && !hasFront) return 1;
    if (hasFront && !hasBack) return 0;
    const name = (item.nname || item.name || "").toLowerCase();
    if (/espalda|back|dorso|posterior/.test(name)) return 1;
    if (/frente|front|pecho|delanter/.test(name)) return 0;
    const parentKey = parentMap[String(cur.key)];
    cur = parentKey ? nodeMap[parentKey] : null;
  }
  return 0;
};

// ¿La ubicación del print es una MANGA? (para espejar el diseño en la vista opuesta y que el
// cliente lo vea de frente y de espalda con un solo asset).
const isSleeveNode = (node, parentMap = {}, nodeMap = {}) => {
  let cur = node;
  let guard = 0;
  while (cur && guard++ < 20) {
    const item = cur.Item || {};
    const name = String(item.nname || item.name || cur.name || "").toLowerCase();
    if (/manga|sleeve/.test(name)) return true;
    const parentKey = parentMap[String(cur.key)];
    cur = parentKey ? nodeMap[parentKey] : null;
  }
  return false;
};

const collectPrintNodes = (root) => {
  const list = [];
  const nodeMap = {};
  const walkNodes = (n) => { if (!n) return; nodeMap[String(n.key)] = n; (n.children || []).forEach(walkNodes); };
  walkNodes(root);
  const parentMap = buildParentMap(root);
  const walk = (node) => {
    if (!node) return;
    if (String(node.type || node.master_part_type) === "6") {
      const side = resolveSide(node, parentMap, nodeMap);
      list.push({ node, side, parentMap, nodeMap });
    }
    (node.children || []).forEach((child) => walk(child));
  };
  walk(root);
  return list;
};

const mapPrintSelection = (printNodes, selectedPrints = {}, allColors = [], side = "front") => {
  const colorById = new Map();
  (allColors || []).forEach((c) => { colorById.set(String(c.id), c); });

  return printNodes
    .flatMap((entry) => {
      const { node } = entry.node ? entry : { node: entry };
      const selections = Object.entries(selectedPrints || {})
        .filter(([printKey, sel]) => String(sel?.attrs?.location_key || printKey).split("::")[0] === String(node.key))
        .sort(comparePrintStackOrder)
        .map(([printKey, sel]) => ({ printKey, sel }));
      if (!selections.length) return [];

      return selections.map(({ printKey, sel }) => {
        // free_scale=1 → el cliente lo agrandó/achicó a mano: NO pisar con el tamaño fijo del tier
        // (mismo criterio que el canvas en vivo). Antes el boceto lo volvía al tamaño original.
        const keepFreeSize = String(sel?.attrs?.free_scale || "") === "1";
        const normalizedSel = (!keepFreeSize && isFixedPredesignSelection(sel))
          ? {
            ...sel,
            attrs: applyPredesignSizeAttrs(sel, sel?.attrs?.predesign_size_tier, sel?.print),
          }
          : sel;
        const effectiveSide = resolveSide(node, entry.parentMap, entry.nodeMap, normalizedSel?.attrs?.side);
        const homeSide = Number(effectiveSide) === 1 ? "back" : "front";
        const onHomeSide = side === homeSide;
        const sleeve = isSleeveNode(node, entry.parentMap, entry.nodeMap);
        // Manga: se ve en AMBAS vistas (espejada en la opuesta). El resto: solo su lado.
        if (!onHomeSide && !sleeve) return null;
        const mirror = !onHomeSide;
        const item = node.Item || {};
        const fallback = LEGACY_PRINT_MAP[node.key] || {};
        const topVal = item.top != null ? item.top : fallback.top;
        const leftVal = item.left != null ? item.left : fallback.left;
        // Escudo de POSICIÓN FIJA: centro absoluto (abs_left/top_pct) en vez del hotspot de la ubicación.
        const absLeftPctRaw = Number(normalizedSel?.attrs?.abs_left_pct);
        const absTopPctRaw = Number(normalizedSel?.attrs?.abs_top_pct);
        const hasAbsPlacement = Number.isFinite(absLeftPctRaw) && Number.isFinite(absTopPctRaw);
        const topPct = hasAbsPlacement ? absTopPctRaw : (topVal != null ? (topVal / BASE_SIZE) * 100 : 0);
        const baseLeftPct = hasAbsPlacement ? absLeftPctRaw : (leftVal != null ? (leftVal / BASE_SIZE) * 100 : 0);
        // En la vista espejada, la manga cae del lado opuesto: reflejamos la posición horizontal.
        const leftPct = mirror ? (100 - baseLeftPct) : baseLeftPct;
        const widthCm = normalizedSel?.attrs?.max_ancho || normalizedSel?.attrs?.maxw || normalizedSel?.print?.max_ancho || normalizedSel?.print?.maxw || fallback.maxw || 4;
        // Escala real lineal: sin el viejo tope 32% (achataba todos los prints grandes al mismo
        // tamaño). Clamp solo como red de seguridad (evitar 0 o desbordar la prenda).
        const widthPct = clamp(cmToWidthPercent(widthCm), 1, 95);
        // TAMAÑO real del print (= su ALTO en cm) con la MISMA prioridad que usa el guardado
        // (resolveRealDimensions de designerPrintSaveEnrichment): real_height_cm / rendered_height_cm
        // primero (ya incluyen la escala), luego bases. Es el mismo valor que muestra el diseñador y
        // que guarda datasheet_prints.size, así que el boceto de la ficha y el MCB quedan idénticos.
        const _num = (v) => { const n = parseFloat(v); return Number.isFinite(n) && n > 0 ? n : 0; };
        const _a = normalizedSel?.attrs || {};
        const _p = normalizedSel?.print || {};
        const printSizeCm = _num(_a.fixed_height_cm) || _num(_a.real_height_cm) || _num(_a.rendered_height_cm) || _num(_a.height)
          || _num(_a.base_height_cm) || _num(_p.max_height) || _num(_p.min_height)
          || _num(_a.real_width_cm) || _num(_a.rendered_width_cm) || _num(_a.max_ancho) || _num(_p.max_width) || 4;
        // Posición fija: sin offset de arrastre.
        const dxRaw = hasAbsPlacement ? 0 : Number(normalizedSel?.attrs?.offset_x_pct || 0);
        const dx = mirror ? -dxRaw : dxRaw;
        const dy = hasAbsPlacement ? 0 : Number(normalizedSel?.attrs?.offset_y_pct || 0);
        const scale = Number(normalizedSel?.attrs?.scale_pct || 100);

        let src = normalizedSel?.attrs?.image_url || null;
        if (!src && normalizedSel?.print?.img_full) src = normalizedSel.print.img_full;
        if (!src && normalizedSel?.print?.img_url) src = normalizedSel.print.img_url;
        if (!src && normalizedSel?.print?.img) {
          const pimg = String(normalizedSel.print.img);
          src = pimg.startsWith("http")
            ? pimg
            : (pimg.includes("/")
              ? pimg // ruta de storage (V4) → la resuelve normalizeSrc
              : `${CDN_BASE}/uploads/charly/prints/${normalizedSel.print.id}/${pimg}`);
        }
        if (!src && normalizedSel?.print?.icon) {
          const picon = String(normalizedSel.print.icon);
          src = picon.includes("/") ? picon : `${CDN_BASE}/uploads/charly/prints/${normalizedSel.print.id}/icon/${picon}`;
        }

        const orderedColorIds = ["color1", "color2", "color3"]
          .map((k) => normalizedSel?.attrs?.[k])
          .filter(Boolean);
        if (!orderedColorIds.length && normalizedSel?.attrs?.color) {
          orderedColorIds.push(normalizedSel.attrs.color);
        }
        const uniqueOrderedColorIds = [];
        const seenColorIds = new Set();
        orderedColorIds.forEach((id) => {
          const normalized = String(id);
          if (!normalized || seenColorIds.has(normalized)) return;
          seenColorIds.add(normalized);
          uniqueOrderedColorIds.push(normalized);
        });
        const colorHexes = uniqueOrderedColorIds
          .map((id) => colorById.get(String(id))?.code)
          .filter(Boolean);
        const colorHex = colorHexes[0] || normalizedSel?.attrs?.color_hex;
        const hasCustomImage = !!normalizedSel?.attrs?.image_url;
        // ¿Se puede pintar? Flag explícito por escudo (master_prints.tintable) con fallback al Set
        // viejo + escudo 6 — mismo criterio que el preview vivo (isTintablePrint en designerHelpers).
        const tintable = !hasCustomImage
          && isTintablePrint(normalizedSel?.print)
          && (Boolean(colorHex) || colorHexes.length > 0);
        const resolvedTechnique = String(normalizedSel?.attrs?.resolved_technique || "").toLowerCase();
        const fontId = normalizedSel?.attrs?.font;
        const fontDef = fontId ? getFontById(fontId) : null;
        const fontFamily = fontDef?.family || "Arial";
        // Escala por fuente del ABM (%, default 100): algunas tipografías se ven más chicas/grandes.
        const fontScale = getFontScale(fontDef);

        return {
          key: `${String(node.key)}::${String(printKey)}`,
          mirror,
          topPct,
          leftPct,
          widthPct,
          // Tamaño real del print (= su ALTO en cm); mismo valor que guarda la ficha. Dimensiona
          // imagen y texto (ver drawPrint). Ya incluye la escala.
          printSizeCm,
          dx,
          dy,
          scale,
          rotation: Number(normalizedSel?.attrs?.rotation_deg || 0) || 0,
          isText: normalizedSel?.mode === "text" || (!!normalizedSel?.attrs?.text && !src),
          text: normalizedSel?.attrs?.text || "",
          textLayout: normalizedSel?.attrs?.text_layout === "vertical" ? "vertical" : "horizontal",
          textShape: normalizedSel?.attrs?.text_shape || "recta",
          textCurvature: Number.isFinite(Number(normalizedSel?.attrs?.text_curvature)) ? Number(normalizedSel.attrs.text_curvature) : 55,
          lineSizes: Array.isArray(normalizedSel?.attrs?.line_sizes) ? normalizedSel.attrs.line_sizes : [],
          fontId,
          fontFamily,
          fontScale,
          src,
          tintable,
          resolvedTechnique,
          colorHex,
          colorHexes,
          textBorder: normalizedSel?.attrs?.text_border,
          // MISMO criterio que el canvas en vivo (PreviewSection): un prediseño/escudo arranca
          // "fijo" (recorte opaco, tamaño por tier), pero si el cliente lo agrandó a mano
          // (free_scale=1) pasa a escalado libre (imagen completa, tamaño por escala). Antes el
          // boceto lo dejaba SIEMPRE fijo → el tamaño "cambiaba" al finalizar respecto del vivo.
          isFixedPredesign: isFixedPredesignSelection(normalizedSel)
            && String(normalizedSel?.attrs?.free_scale || "") !== "1",
          zIndex: (item.z || 0) + 10,
        };
      });
    })
    .filter(Boolean);
};

const drawMaskedFill = async (ctx, imgUrl, { colorHex, patternUrl }, size) => {
  const mask = await safeLoadImage(imgUrl);
  if (!mask) return;
  const tmp = document.createElement("canvas");
  tmp.width = size;
  tmp.height = size;
  const tctx = tmp.getContext("2d");

  let patternDrawn = false;
  if (patternUrl) {
    const patt = await safeLoadImage(patternUrl);
    if (patt) {
      tctx.drawImage(patt, 0, 0, patt.naturalWidth, patt.naturalHeight, 0, 0, size, size);
      patternDrawn = true;
    } else {
      const idMatch = patternUrl.match(/fullprints\/(?:900x900|34x34)\/([^./]+)/i);
      const code = idMatch ? idMatch[1] : "fp";
      const hue = (parseInt(code.replace(/\D/g, "") || "0", 10) * 37) % 360;
      const grad = tctx.createLinearGradient(0, 0, size, size);
      grad.addColorStop(0, `hsl(${hue}, 75%, 60%)`);
      grad.addColorStop(1, `hsl(${(hue + 50) % 360}, 80%, 50%)`);
      tctx.fillStyle = grad;
      tctx.fillRect(0, 0, size, size);
      patternDrawn = true;
    }
  }
  if (!patternDrawn) {
    tctx.fillStyle = colorHex || "#ffffff";
    tctx.fillRect(0, 0, size, size);
  }

  tctx.globalCompositeOperation = "destination-in";
  tctx.drawImage(mask, 0, 0, mask.naturalWidth, mask.naturalHeight, 0, 0, size, size);
  ctx.drawImage(tmp, 0, 0);
};

const drawCustomCutZoneOverlaysForLayer = async ({
  ctx,
  maskImg,
  zoneOverlays = [],
  size,
}) => {
  if (
    !ctx
    || !maskImg
    || !Array.isArray(zoneOverlays)
    || !zoneOverlays.length
    || size <= 1
  ) {
    return;
  }
  const resolvedMaskImg = typeof maskImg === "string"
    ? await safeLoadImage(maskImg)
    : maskImg;
  if (!resolvedMaskImg) return;
  const overlayCanvas = document.createElement("canvas");
  overlayCanvas.width = size;
  overlayCanvas.height = size;
  const overlayCtx = overlayCanvas.getContext("2d");
  if (!overlayCtx) return;
  const bleedPx = Math.max(1, Math.round(size / 700));
  const bleedOffsets = [
    [0, 0],
    [-bleedPx, 0],
    [bleedPx, 0],
    [0, -bleedPx],
    [0, bleedPx],
    [-bleedPx, -bleedPx],
    [-bleedPx, bleedPx],
    [bleedPx, -bleedPx],
    [bleedPx, bleedPx],
  ];

  zoneOverlays.forEach((zone) => {
    if (!zone?.color?.code || !zone?.maskCanvas) return;
    const zoneCanvas = document.createElement("canvas");
    zoneCanvas.width = size;
    zoneCanvas.height = size;
    const zoneCtx = zoneCanvas.getContext("2d");
    if (!zoneCtx) return;
    bleedOffsets.forEach(([offsetX, offsetY]) => {
      zoneCtx.drawImage(zone.maskCanvas, offsetX, offsetY, size, size);
    });
    zoneCtx.globalCompositeOperation = "source-in";
    zoneCtx.fillStyle = zone.color.code;
    zoneCtx.fillRect(0, 0, size, size);
    zoneCtx.globalCompositeOperation = "source-over";
    overlayCtx.drawImage(zoneCanvas, 0, 0, size, size);
  });

  overlayCtx.globalCompositeOperation = "destination-in";
  overlayCtx.drawImage(
    resolvedMaskImg,
    0,
    0,
    resolvedMaskImg.naturalWidth,
    resolvedMaskImg.naturalHeight,
    0,
    0,
    size,
    size
  );
  overlayCtx.globalCompositeOperation = "source-over";
  ctx.drawImage(overlayCanvas, 0, 0, size, size);
};

const drawImageLayer = async (ctx, imgUrl, size) => {
  const img = await safeLoadImage(imgUrl);
  if (!img) return;
  ctx.drawImage(img, 0, 0, size, size);
};

const drawEmbroideryTextLayer = (ctx, lines, lineHeight, fillColor, strokeColor) => {
  const fontSize = Number(ctx?.font?.match?.(/(\d+)px/)?.[1] || 16);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.miterLimit = 2;
  ctx.lineWidth = Math.max(1, fontSize * 0.12);
  ctx.strokeStyle = strokeColor || fillColor || "#1f2a3d";
  ctx.fillStyle = fillColor || "#ffffff";
  lines.forEach((line, idx) => {
    const y = (idx - (lines.length - 1) / 2) * lineHeight;
    const safeLine = String(line || " ");
    ctx.strokeText(safeLine, 0, y);
    ctx.fillText(safeLine, 0, y);
  });
};

const drawEmbroideryPredesignLayer = (ctx, img, drawW, drawH, fillColor, strokeColor, sourceRect = null) => {
  const offW = Math.max(1, Math.round(drawW));
  const offH = Math.max(1, Math.round(drawH));
  const sx = Number(sourceRect?.x || 0);
  const sy = Number(sourceRect?.y || 0);
  const sw = Math.max(1, Number(sourceRect?.width || img?.naturalWidth || img?.width || 1));
  const sh = Math.max(1, Number(sourceRect?.height || img?.naturalHeight || img?.height || 1));

  const fillCanvas = document.createElement("canvas");
  fillCanvas.width = offW;
  fillCanvas.height = offH;
  const fillCtx = fillCanvas.getContext("2d");
  if (!fillCtx) return;
  fillCtx.drawImage(img, sx, sy, sw, sh, 0, 0, offW, offH);
  fillCtx.globalCompositeOperation = "source-in";
  fillCtx.fillStyle = fillColor || "#ffffff";
  fillCtx.fillRect(0, 0, offW, offH);
  fillCtx.globalCompositeOperation = "source-over";

  const strokeSource = document.createElement("canvas");
  strokeSource.width = offW;
  strokeSource.height = offH;
  const strokeSourceCtx = strokeSource.getContext("2d");
  if (!strokeSourceCtx) return;
  strokeSourceCtx.drawImage(img, sx, sy, sw, sh, 0, 0, offW, offH);
  strokeSourceCtx.globalCompositeOperation = "source-in";
  strokeSourceCtx.fillStyle = strokeColor || fillColor || "#1f2a3d";
  strokeSourceCtx.fillRect(0, 0, offW, offH);
  strokeSourceCtx.globalCompositeOperation = "source-over";

  const strokeCanvas = document.createElement("canvas");
  strokeCanvas.width = offW;
  strokeCanvas.height = offH;
  const strokeCtx = strokeCanvas.getContext("2d");
  if (!strokeCtx) return;
  const thickness = Math.max(1, Math.round(Math.max(offW, offH) * 0.025));
  const offsets = [
    [-thickness, 0], [thickness, 0], [0, -thickness], [0, thickness],
    [-thickness, -thickness], [-thickness, thickness], [thickness, -thickness], [thickness, thickness],
  ];
  offsets.forEach(([ox, oy]) => strokeCtx.drawImage(strokeSource, ox, oy));
  strokeCtx.globalCompositeOperation = "destination-out";
  strokeCtx.drawImage(fillCanvas, 0, 0);
  strokeCtx.globalCompositeOperation = "source-over";

  ctx.drawImage(strokeCanvas, -drawW / 2, -drawH / 2, drawW, drawH);
  ctx.drawImage(fillCanvas, -drawW / 2, -drawH / 2, drawW, drawH);
};

const drawPrint = async (ctx, print, size) => {
  const centerX = ((print.leftPct + print.dx) / 100) * size;
  const centerY = ((print.topPct + print.dy) / 100) * size;
  const rotationRad = ((Number(print.rotation || 0) % 360) * Math.PI) / 180;

  if (print.isText) {
    ctx.save();
    ctx.translate(centerX, centerY);
    if (print.mirror) ctx.scale(-1, 1);
    if (rotationRad) ctx.rotate(rotationRad);
    const isEmbroidery = String(print.resolvedTechnique || "").toLowerCase() === "bordado";
    // Relleno + CONTORNO (2 colores) = bordado O aplique/estampado con borde (text_border=1).
    // MISMO criterio que el live (PreviewSection.wantsTextOutline). Sin esto, el aplique caía en
    // el modo "multicolor por carácter" de drawShapedText → cada letra de un color distinto.
    const wantsTextOutline = isEmbroidery || String(print.textBorder || "") === "1";
    if (wantsTextOutline && print.colorHexes && print.colorHexes.length > 1) {
      ctx.fillStyle = print.colorHexes[0] || "#ffffff";
    } else {
      ctx.fillStyle = print.colorHex || "#222";
    }
    // Tamaño del TEXTO a ESCALA REAL: el tamaño del print (cm, el que muestra el diseñador) es
    // su ALTO de letra, con la escala VERTICAL (11 px/cm). fontSize ≈ alto de letra (cap height
    // ~0,7·em → factor 1,4). printSizeCm ya incluye la escala. Clamp de seguridad.
    const pxPerCm = size / PRINT_SCALE_CM_H;
    const letterHeightCm = Number(print.printSizeCm) > 0 ? Number(print.printSizeCm) : 3;
    // Escala por fuente del ABM (%, 100 = sin cambio).
    const fontScalePct = Number(print.fontScale) > 0 ? Number(print.fontScale) : 100;
    const fontSize = clamp(letterHeightCm * pxPerCm * 1.4 * (fontScalePct / 100), 10, size * 0.6);
    // Fallback explícito a sans: si la fuente no tiene archivo cargado en el ABM (ej. Molot/John
    // Hancock) el @font-face falla → que caiga a Arial/sans en vez de a la serif por defecto.
    ctx.font = `${Math.round(fontSize)}px '${print.fontFamily || "Arial"}', Arial, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const palette = (print.colorHexes && print.colorHexes.length
      ? print.colorHexes
      : (print.colorHex ? [print.colorHex] : ["#222"])).filter(Boolean);
    if (print.textLayout === "vertical") {
      const lines = String(print.text || "").split(/\n/);
      const lineHeight = fontSize * 1.05;
      if (wantsTextOutline && palette.length > 1) {
        drawEmbroideryTextLayer(ctx, lines, lineHeight, palette[0] || "#ffffff", palette[1] || "#1f2a3d");
      } else {
        lines.forEach((line, idx) => {
          ctx.fillText(line, 0, (idx - (lines.length - 1) / 2) * lineHeight);
        });
      }
    } else {
      // Mismo renderer que el preview → el boceto refleja forma + tamaño por renglón.
      const rawLines = String(print.text || "").split(/\n/);
      const shapedLines = rawLines.map((t, i) => ({
        text: t,
        size: Number(print.lineSizes?.[i]) > 0 ? Number(print.lineSizes[i]) : 1,
      }));
      drawShapedText(ctx, {
        lines: shapedLines,
        baseFontSize: fontSize,
        fontFamily: print.fontFamily || "Arial",
        shape: print.textShape || "recta",
        curvature: print.textCurvature,
        palette,
        embroidery: wantsTextOutline && palette.length > 1,
      });
    }
    ctx.restore();
    return;
  }

  if (!print.src) return;
  const img = await safeLoadImage(print.src);
  if (!img) return;

  const sourceRect = print.isFixedPredesign
    ? getImageOpaqueBounds(img)
    : {
      x: 0,
      y: 0,
      width: Number(img.naturalWidth || img.width || 1),
      height: Number(img.naturalHeight || img.height || 1),
      aspectRatio: Number(img.naturalWidth || img.width || 1) / Math.max(1, Number(img.naturalHeight || img.height || 1)),
    };
  const sourceW = Math.max(1, Number(sourceRect.width || img.naturalWidth || img.width || 1));
  const sourceH = Math.max(1, Number(sourceRect.height || img.naturalHeight || img.height || 1));
  const aspectRatio = Math.max(0.05, Number(sourceRect.aspectRatio || (sourceW / sourceH) || 1));
  // El TAMAÑO del print es su ALTO (regla del negocio): se dimensiona por alto real de dibujo
  // con la escala vertical (11 px/cm); el ancho sale del aspecto. Un print de 25 cm ≈ 42% de la
  // espalda. (Antes se escalaba por ancho → salía ~26%, chico.)
  const printHeightCm = Number(print.printSizeCm) > 0 ? Number(print.printSizeCm) : 4;
  const targetHeightPx = printHeightCm * (size / PRINT_SCALE_CM_H);
  const targetWidthPx = targetHeightPx * aspectRatio;
  const fitScale = Math.min(targetWidthPx / sourceW, targetHeightPx / sourceH);
  const drawW = Math.max(1, sourceW * fitScale);
  const drawH = Math.max(1, sourceH * fitScale);

  ctx.save();
  ctx.translate(centerX, centerY);
  if (print.mirror) ctx.scale(-1, 1);
  if (rotationRad) ctx.rotate(rotationRad);
  if (print.tintable && (print.colorHex || (print.colorHexes && print.colorHexes.length))) {
    const off = document.createElement("canvas");
    off.width = Math.max(1, Math.round(drawW));
    off.height = Math.max(1, Math.round(drawH));
    const offCtx = off.getContext("2d");
    offCtx.drawImage(
      img,
      Number(sourceRect.x || 0),
      Number(sourceRect.y || 0),
      sourceW,
      sourceH,
      0,
      0,
      off.width,
      off.height
    );
    offCtx.globalCompositeOperation = "source-in";
    const isEmbroidery = String(print.resolvedTechnique || "").toLowerCase() === "bordado";
    if (isEmbroidery && print.colorHexes && print.colorHexes.length > 1) {
      drawEmbroideryPredesignLayer(
        ctx,
        img,
        drawW,
        drawH,
        print.colorHexes[0] || "#ffffff",
        print.colorHexes[1] || "#1f2a3d",
        sourceRect
      );
      ctx.restore();
      return;
    } else if (print.colorHexes && print.colorHexes.length > 1) {
      offCtx.fillStyle = print.colorHexes[0] || "#ffffff";
    } else {
      offCtx.fillStyle = print.colorHex || "#ffffff";
    }
    offCtx.fillRect(0, 0, off.width, off.height);
    offCtx.globalCompositeOperation = "source-over";
    ctx.drawImage(off, -drawW / 2, -drawH / 2, drawW, drawH);
  } else {
    ctx.drawImage(
      img,
      Number(sourceRect.x || 0),
      Number(sourceRect.y || 0),
      sourceW,
      sourceH,
      -drawW / 2,
      -drawH / 2,
      drawW,
      drawH
    );
  }
  ctx.restore();
};

const buildGarmentMask = (ctx, size) => {
  const width = Math.max(1, Math.round(Number(size || BASE_SIZE)));
  const height = width;
  const sourceData = ctx.getImageData(0, 0, width, height).data;
  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = width;
  maskCanvas.height = height;
  const maskCtx = maskCanvas.getContext("2d");
  const imageData = maskCtx.createImageData(width, height);

  for (let i = 0; i < sourceData.length; i += 4) {
    const alpha = sourceData[i + 3];
    if (alpha <= 8) continue;
    imageData.data[i] = 255;
    imageData.data[i + 1] = 255;
    imageData.data[i + 2] = 255;
    imageData.data[i + 3] = 255;
  }
  maskCtx.putImageData(imageData, 0, 0);
  return maskCanvas;
};

const drawPrintClipped = async (ctx, print, size, maskCanvas) => {
  if (!maskCanvas) {
    await drawPrint(ctx, print, size);
    return;
  }
  const layer = document.createElement("canvas");
  layer.width = size;
  layer.height = size;
  const layerCtx = layer.getContext("2d");
  if (!layerCtx) {
    await drawPrint(ctx, print, size);
    return;
  }
  await drawPrint(layerCtx, print, size);
  layerCtx.globalCompositeOperation = "destination-in";
  layerCtx.drawImage(maskCanvas, 0, 0, size, size);
  layerCtx.globalCompositeOperation = "source-over";
  ctx.drawImage(layer, 0, 0);
};

const drawStripeStroke = (ctx, stroke, size) => {
  if (!ctx || !stroke) return;
  const points = Array.isArray(stroke.points_norm) ? stroke.points_norm : [];
  if (!points.length) return;
  ctx.save();
  ctx.globalAlpha = clamp(Number(stroke.opacity ?? 0.94), 0.88, 1);
  ctx.strokeStyle = String(stroke.color || "#1d4ed8");
  ctx.lineWidth = Math.max(2, Number(stroke.thickness || 8));
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.beginPath();
  const start = points[0] || [0.5, 0.5];
  ctx.moveTo(clamp(Number(start[0] || 0) * size, 0, size), clamp(Number(start[1] || 0) * size, 0, size));
  if (String(stroke.tool || "").toLowerCase() === "line" && points.length >= 2) {
    const end = points[1] || start;
    ctx.lineTo(clamp(Number(end[0] || 0) * size, 0, size), clamp(Number(end[1] || 0) * size, 0, size));
  } else {
    for (let i = 1; i < points.length; i += 1) {
      const p = points[i] || points[i - 1];
      ctx.lineTo(clamp(Number(p[0] || 0) * size, 0, size), clamp(Number(p[1] || 0) * size, 0, size));
    }
  }
  ctx.stroke();
  ctx.restore();
};

const drawStripeLayer = ({ ctx, garmentMask, stripeDrawingsBySide = null, side = "front", size }) => {
  if (!ctx || !garmentMask) return;
  const sideKey = side === "back" ? "back" : "front";
  const stripeStrokes = Array.isArray(stripeDrawingsBySide?.[sideKey]) ? stripeDrawingsBySide[sideKey] : [];
  if (!stripeStrokes.length) return;
  const stripeCanvas = document.createElement("canvas");
  stripeCanvas.width = size;
  stripeCanvas.height = size;
  const stripeCtx = stripeCanvas.getContext("2d");
  if (!stripeCtx) return;
  stripeStrokes.forEach((stroke) => drawStripeStroke(stripeCtx, stroke, size));
  stripeCtx.globalCompositeOperation = "destination-in";
  stripeCtx.drawImage(garmentMask, 0, 0, size, size);
  stripeCtx.globalCompositeOperation = "source-over";
  ctx.drawImage(stripeCanvas, 0, 0);
};

const normalizeAdjacentCutIds = (value = []) =>
  Array.isArray(value)
    ? value.map((cutId) => String(cutId || "").trim()).filter(Boolean).sort()
    : [];

const buildCustomCutZoneMatchKey = (zone = null) => {
  const componentKey = String(zone?.componentKey || "").trim();
  const label = String(zone?.label || "").trim().toLowerCase();
  const adjacentCutIds = normalizeAdjacentCutIds(zone?.adjacentCutIds);
  return `${componentKey}::${adjacentCutIds.join("|")}::${label}`;
};

const hydrateCustomCutRuntimeColors = ({
  runtime = null,
  customCutZonesBySide = null,
  side = "front",
}) => {
  if (!runtime || !Array.isArray(runtime?.zones)) return runtime;
  const sourceZones = Array.isArray(customCutZonesBySide?.[side]) ? customCutZonesBySide[side] : [];
  if (!sourceZones.length) return runtime;

  const sourceByKey = new Map();
  sourceZones.forEach((zone) => {
    const key = buildCustomCutZoneMatchKey(zone);
    if (!key || sourceByKey.has(key)) return;
    sourceByKey.set(key, zone);
  });

  const sourceByComponent = new Map();
  sourceZones.forEach((zone) => {
    const componentKey = String(zone?.componentKey || "").trim();
    if (!componentKey) return;
    if (!sourceByComponent.has(componentKey)) {
      sourceByComponent.set(componentKey, []);
    }
    sourceByComponent.get(componentKey).push(zone);
  });

  const runtimeByComponent = new Map();
  runtime.zones.forEach((zone) => {
    const componentKey = String(zone?.componentKey || "").trim();
    if (!componentKey) return;
    if (!runtimeByComponent.has(componentKey)) {
      runtimeByComponent.set(componentKey, []);
    }
    runtimeByComponent.get(componentKey).push(zone);
  });

  const hydratedZones = runtime.zones.map((zone) => {
    const exact = sourceByKey.get(buildCustomCutZoneMatchKey(zone));
    if (exact?.color?.code || exact?.color?.name) {
      return {
        ...zone,
        color: exact.color,
      };
    }
    const componentKey = String(zone?.componentKey || "").trim();
    const runtimeGroup = runtimeByComponent.get(componentKey) || [];
    const sourceGroup = sourceByComponent.get(componentKey) || [];
    const runtimeIndex = runtimeGroup.findIndex((entry) => String(entry?.id || "") === String(zone?.id || ""));
    const fallback = runtimeIndex >= 0 ? sourceGroup[runtimeIndex] : null;
    if (fallback?.color?.code || fallback?.color?.name) {
      return {
        ...zone,
        color: fallback.color,
      };
    }
    return zone;
  });

  return {
    ...runtime,
    zones: hydratedZones,
    zonesById: Object.fromEntries(hydratedZones.map((zone) => [String(zone?.id || ""), zone])),
  };
};

const renderSide = async ({
  side,
  parts,
  root,
  selectedPrints,
  allColors,
  size,
  stripeDrawingsBySide = null,
  customCutsBySide = null,
  customCutZoneColors = null,
  customCutZonesBySide = null,
  customCutRuntimeBySide = null,
}) => {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  const layers = buildLayersForSide(parts, side);
  const maskLayers = layers.filter((layer) => layer.kind === "mask");

  for (const layer of maskLayers) {
    if (layer.kind === "mask") {
      await drawMaskedFill(ctx, layer.img, { colorHex: layer.colorHex, patternUrl: layer.patternUrl }, size);
    } else {
      await drawImageLayer(ctx, layer.img, size);
    }
  }
  const garmentMask = buildGarmentMask(ctx, size);

  const stripeLayerZ = (() => {
    const maskZ = maskLayers.map((layer) => Number(layer.zIndex || 0));
    const maxMaskZ = maskZ.length ? Math.max(...maskZ) : 0;
    return maxMaskZ + 1;
  })();
  const previewResolvedRuntime = customCutRuntimeBySide?.[side] || null;
  const rawCustomCutRuntime = previewResolvedRuntime || await buildCustomCutRuntime({
    layers,
    side,
    width: size,
    height: size,
    customCutsBySide,
    customCutZoneColors,
  });
  const customCutRuntime = previewResolvedRuntime || hydrateCustomCutRuntimeColors({
    runtime: rawCustomCutRuntime,
    customCutZonesBySide,
    side,
  });
  const customCutZoneOverlaysByPart = (() => {
    const grouped = {};
    const zones = Array.isArray(customCutRuntime?.zones) ? customCutRuntime.zones : [];
    zones.forEach((zone) => {
      if (!zone?.color?.code || !zone?.maskCanvas) return;
      const partKeys = Array.isArray(zone?.partKeys) ? zone.partKeys : [];
      partKeys.forEach((partKey) => {
        const resolvedKey = String(partKey || "");
        if (!resolvedKey) return;
        if (!Array.isArray(grouped[resolvedKey])) {
          grouped[resolvedKey] = [];
        }
        grouped[resolvedKey].push(zone);
      });
    });
    return grouped;
  })();

  ctx.clearRect(0, 0, size, size);
  let stripesDrawn = false;
  for (const layer of layers) {
    const layerZ = Number(layer.zIndex || 0);
    if (!stripesDrawn && layerZ > stripeLayerZ) {
      drawStripeLayer({
        ctx,
        garmentMask,
        stripeDrawingsBySide,
        side,
        size,
      });
      stripesDrawn = true;
    }

    if (layer.kind === "mask") {
      await drawMaskedFill(ctx, layer.img, { colorHex: layer.colorHex, patternUrl: layer.patternUrl }, size);
      await drawCustomCutZoneOverlaysForLayer({
        ctx,
        maskImg: layer.img,
        zoneOverlays: customCutZoneOverlaysByPart[String(layer?.partKey || "")] || [],
        size,
      });
    } else {
      await drawImageLayer(ctx, layer.img, size);
    }
  }

  if (!stripesDrawn) {
    drawStripeLayer({
      ctx,
      garmentMask,
      stripeDrawingsBySide,
      side,
      size,
    });
  }

  const printNodes = collectPrintNodes(root);
  const prints = mapPrintSelection(printNodes, selectedPrints, allColors, side);
  for (const p of prints) {
    await drawPrintClipped(ctx, p, size, garmentMask);
  }

  return canvas;
};

export const renderSketchImage = async ({
  selectedParts = [],
  root,
  selectedPrints = {},
  allColors = [],
  stripeDrawingsBySide = null,
  customCutsBySide = null,
  customCutZoneColors = null,
  customCutZonesBySide = null,
  customCutRuntimeBySide = null,
  layout: _layout = "vertical",
  // false = fondo limpio, sin la decoración de marca (globo, logos, texto "LITTLE BLUE",
  // cuadrado). Se usa en la preview del RESUMEN del cliente; el boceto de la ficha la mantiene.
  decorations = true,
}) => {
  const size = BASE_SIZE;
  const [frontCanvas, backCanvas] = await Promise.all([
    renderSide({ side: "front", parts: selectedParts, root, selectedPrints, allColors, size, stripeDrawingsBySide, customCutsBySide, customCutZoneColors, customCutZonesBySide, customCutRuntimeBySide }),
    renderSide({ side: "back", parts: selectedParts, root, selectedPrints, allColors, size, stripeDrawingsBySide, customCutsBySide, customCutZoneColors, customCutZonesBySide, customCutRuntimeBySide }),
  ]);

  const margin = 60;
  const labelHeight = 0;
  // "horizontal" → frente y espalda lado a lado; cualquier otro valor → apilados (vertical).
  const isMobileLayout = _layout !== "horizontal";
  const finalCanvas = document.createElement("canvas");
  if (isMobileLayout) {
    finalCanvas.width = size + margin * 2;
    finalCanvas.height = size * 2 + labelHeight + margin * 3;
  } else {
    finalCanvas.width = size * 2 + margin * 3;
    finalCanvas.height = size + labelHeight + margin * 2;
  }
  const ctx = finalCanvas.getContext("2d");
  ctx.fillStyle = "#f7f7f7";
  ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
  if (!isMobileLayout) {
    if (decorations) {
      await drawLegacyMergedBackground(ctx, {
        x: margin,
        y: labelHeight,
        size,
        height: size,
        layout: "horizontal",
      });
    }
    ctx.drawImage(frontCanvas, margin, labelHeight, size, size);
    ctx.drawImage(backCanvas, margin * 2 + size, labelHeight, size, size);
  } else {
    if (decorations) {
      await drawVerticalBackground(ctx, {
        width: finalCanvas.width,
        height: finalCanvas.height,
      });
    }
    ctx.drawImage(frontCanvas, margin, labelHeight, size, size);
    ctx.drawImage(backCanvas, margin, labelHeight + margin + size, size, size);
  }

  return finalCanvas.toDataURL("image/png");
};

export default renderSketchImage;
